/* eslint-disable no-unsafe-optional-chaining */
import useCommon from '@utils/hooks/common';
import MultiUnitStatistic from '@components/game-room/MultiUnitStatistic';
import React, {useState, useEffect, useRef} from 'react';
import {Statistic} from 'antd';
import styles from './styles';
import {TournamentContext} from '@utils/context';
import {formatCompactNumber} from '@utils/index';
import dayjs from 'dayjs';
import Countdown, {CountdownApi, zeroPad} from 'react-countdown';

// const {Countdown} = Statistic;

interface GameInfoAreaProps {
  sb?: number;
  bb?: number;
  gameMode: string;
}

const GameInfoArea = React.memo((props: GameInfoAreaProps) => {
  const {classes, t} = useCommon({
    withStyles: styles,
    withTranslation: ['table_area', 'best_combination', 'common'],
  });

  const numberFormatter2decimal = (value: number) => {
    if (value % 1 !== 0) {
      return value.toFixed(2);
    }
    return value;
  };

  const {
    isTournament,
    currentSmallBlind: tournamentSb,
    currentBigBlind: tournamentBb,
    nextSmallBlind: tournamentNextSb,
    nextBigBlind: tournamentNextBb,
    nextBlindAt,
    tournamentStartTime,
    tournamentWaitingStatus,
    tournamentWaitingStatusData,
  } = React.useContext(TournamentContext);

  const getCurrentSb = () => {
    if (isTournament) {
      return tournamentSb;
    }
    return props?.sb;
  };

  const getCurrentBb = () => {
    if (isTournament) {
      return tournamentBb;
    }
    return props?.bb;
  };

  // Cause UI update when countdown completed
  const [countdownCompleted, setCountdownCompleted] = useState(false);

  // Determine if tournament has started based on time comparison and player readiness
  const isTournamentStarted =
    countdownCompleted ||
    (tournamentStartTime ? new Date(tournamentStartTime) <= new Date() : false);
  
  // Check if tournament is waiting for enough players to start
  const isWaitingForPlayers = tournamentWaitingStatus === 'WAITING_ENOUGH_PLAYERS_TO_START';
  
  // Check if tournament is in upcoming status (before start time)
  const isUpcoming = tournamentWaitingStatus === 'UPCOMING';
  
  // Check if tournament is starting (enough players joined)
  const isStarting = tournamentWaitingStatus === 'STARTING';
  
  // Reset countdown completed when waiting status changes
  React.useEffect(() => {
    if (isWaitingForPlayers) {
      setCountdownCompleted(true);
    }
  }, [isWaitingForPlayers]);

  const renderer = ({minutes, seconds}) => {
    return <span>{`${zeroPad(minutes)}:${zeroPad(seconds)}`}</span>;
  };

  const renderTournamentBlindsOrStartGameCountdown = () => {
    const now = new Date();
    const startTime = tournamentStartTime ? new Date(tournamentStartTime) : null;
    // Add a small buffer (1 second) to account for timing differences
    const hasReachedStartTime = startTime && startTime <= new Date(now.getTime() + 1000);
    
    // Debug logging
    console.log('GameInfoArea render:', {
      isTournament,
      isWaitingForPlayers,
      isUpcoming,
      isStarting,
      tournamentWaitingStatus,
      tournamentWaitingStatusData,
      isTournamentStarted,
      tournamentStartTime,
      startTime: startTime?.toISOString(),
      now: now.toISOString(),
      hasReachedStartTime,
      countdownCompleted
    });
    
    // Case 1: Waiting for players after start time has been reached
    if (isWaitingForPlayers && hasReachedStartTime) {
      // Show waiting message with player count and countdown
      const currentPlayers = tournamentWaitingStatusData?.currentPlayers || 0;
      const requiredPlayers = tournamentWaitingStatusData?.minPlayersRequired || 0;
      const cancelTime = tournamentWaitingStatusData?.cancelTime;
      
      if (cancelTime) {
        return (
          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '2px'}}>
            <div>{t('tournament:waiting_enough_players_to_start')}</div>
            <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
              <span>{currentPlayers}/{requiredPlayers}</span>
              <span>|</span>
              <Countdown date={new Date(cancelTime)} renderer={renderer} key={`tournament-cancel-${cancelTime}`} />
            </div>
          </div>
        );
      } else {
        // Fallback to remainingTimeMinutes if cancelTime not available
        const remainingMinutes = tournamentWaitingStatusData?.remainingTimeMinutes || 0;
        const minutes = Math.floor(remainingMinutes);
        const seconds = Math.round((remainingMinutes - minutes) * 60);
        const timeDisplay = `${zeroPad(minutes)}:${zeroPad(seconds)}`;
        
        return (
          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '2px'}}>
            <div>{t('tournament:waiting_enough_players_to_start')}</div>
            <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
              <span>{currentPlayers}/{requiredPlayers}</span>
              <span>|</span>
              <span>{timeDisplay}</span>
            </div>
          </div>
        );
      }
    }
    
    // Case 2: Tournament is starting (enough players joined)
    if (isStarting) {
      // Hide the table text when tournament is starting
      return null;
    }
    
    // Case 3: Tournament has started with enough players
    if (isTournamentStarted && !isWaitingForPlayers && !isStarting) {
      return (
        <>
          {t('tournament:next_blind_at')} |
          <div style={{margin: '0 5px'}}>
            {formatCompactNumber(tournamentNextSb)} / {formatCompactNumber(tournamentNextBb)}
          </div>
          |
          <span style={{marginLeft: 5}}>
            <Countdown
              date={dayjs(nextBlindAt).toDate()}
              renderer={renderer}
              key={`next-blind-${nextBlindAt}`}
            />
          </span>
          {/* <Countdown
            value={nextBlindAt}
            format="mm:ss"
            valueStyle={{
              fontSize: 12,
              color: 'white',
              margin: '0 5px',
              lineHeight: 1,
            }}
          /> */}
        </>
      );
    }
    
    // Case 4: Tournament hasn't started yet - show "Start On mm:ss" countdown
    // This should show when status is "UPCOMING" or when we don't have a status yet but haven't reached start time
    if (isUpcoming || (!hasReachedStartTime && startTime && !isWaitingForPlayers && !isStarting && !tournamentWaitingStatus)) {
      return (
        <>
          {t('tournament:start_game_in')}
          <span style={{marginLeft: 5}}>
            <Countdown
              date={startTime}
              renderer={renderer}
              key={`tournament-start-${tournamentStartTime}`}
              onComplete={() => {
                console.log('Tournament start time reached', tournamentStartTime);
                setCountdownCompleted(true);
                // The server should send a TOURNAMENT_WAITING_STATUS_UPDATE event
              }}
            />
          </span>
        </>
      );
    }
    
    // Case 4: Start time reached but no waiting status yet - show nothing to avoid stuck 00:00
    if (hasReachedStartTime && !isWaitingForPlayers && !getCurrentBb()) {
      return null;
    }
    
    // Default case - should not happen in normal flow
    return null;
  };

  const renderCurrentBlinds = () => {
    if (isTournament && (!isTournamentStarted || isWaitingForPlayers)) {
      return null;
    }

    return (
      <div className={classes.gameMode}>
        {isTournament
          ? t('tournament:current_blinds')
          : t(`common:${props.gameMode?.toLocaleLowerCase()}`)}
        {' |'}
        <Statistic
          className={classes.multiUnitContainer}
          value={getCurrentSb()}
          valueStyle={{
            fontSize: 12,
            color: 'white',
            margin: '0 5px',
            lineHeight: 1,
          }}
          prefix={!isTournament ? '$' : ''}
          formatter={numberFormatter2decimal}
        />
        /
        <Statistic
          className={classes.multiUnitContainer}
          value={getCurrentBb()}
          valueStyle={{
            fontSize: 12,
            color: 'white',
            margin: '0 5px',
            lineHeight: 1,
          }}
          prefix={!isTournament ? '$' : ''}
          formatter={numberFormatter2decimal}
        />
      </div>
    );
  };

  return (
    <section className={classes.gameInfoArea} style={{top: isTournament ? '24.6%' : '28%'}}>
      {renderCurrentBlinds()}
      {isTournament && (
        <div className={classes.tournamentInfoContainer}>
          {renderTournamentBlindsOrStartGameCountdown()}
        </div>
      )}
    </section>
  );
});

GameInfoArea.displayName = 'GameInfoArea';

export default GameInfoArea;
